import axios from 'axios';

// Configuration de base pour l'API
import API_BASE_URL from '../config/api';

// Créer une instance axios avec configuration par défaut
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // Augmenté à 30 secondes pour les requêtes analytics complexes
            headers: {
                'Content-Type': 'application/json',
            },
});

// Intercepteur pour ajouter le token d'authentification
apiClient.interceptors.request.use(
  (config) => {
    // Chercher d'abord le token restaurant, puis le token access_token pour compatibilité
    const token = localStorage.getItem('restaurantToken') || localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Messages génériques et compréhensibles, utilisés quand le backend ne renvoie pas de `detail`
// exploitable (sinon error.message affiche le texte brut d'axios, ex: "Request failed with status code 401")
const FRIENDLY_STATUS_MESSAGES = {
  401: 'Votre session a expiré. Veuillez vous reconnecter.',
  403: "Vous n'avez pas les droits nécessaires pour cette action.",
  404: 'Ressource introuvable.',
  500: 'Erreur serveur. Veuillez réessayer plus tard.',
};

// Intercepteur pour gérer les erreurs de réponse
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Déconnecter et rediriger uniquement sur 401 (token invalide/expiré)
    // Ne pas rediriger si on vient de se connecter (évite déconnexion immédiate due à une requête en cours)
    const justLoggedIn = sessionStorage.getItem('restaurantJustLoggedIn') === 'true';
    if (error.response?.status === 401 && !justLoggedIn) {
      localStorage.removeItem('restaurantToken');
      localStorage.removeItem('restaurantEmail');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/business/login';
    }
    if (!error.response?.data?.detail) {
      error.message = FRIENDLY_STATUS_MESSAGES[error.response?.status] || 'Une erreur est survenue. Veuillez réessayer.';
    }
    return Promise.reject(error);
  }
);

// === GESTION DES PLATS ===

// Récupérer tous les plats du restaurant
export const getRestaurantDishes = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/dishes', { params });
    return response.data;
    } catch (error) {
    console.error('Erreur lors de la récupération des plats:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des plats');
  }
};

// Créer un nouveau plat
export const createDish = async (dishData) => {
  try {
    const response = await apiClient.post('/management/dishes', dishData);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la création du plat:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la création du plat');
  }
};

// Mettre à jour un plat
export const updateDish = async (dishId, dishData) => {
  try {
    const response = await apiClient.put(`/management/dishes/${dishId}`, dishData);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du plat:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour du plat');
  }
};

// Supprimer un plat
export const deleteDish = async (dishId) => {
  try {
    const response = await apiClient.delete(`/management/dishes/${dishId}`);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la suppression du plat:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la suppression du plat');
  }
};

// Basculer la disponibilité d'un plat
export const toggleDishAvailability = async (dishId, isActive) => {
  try {
    const response = await apiClient.put(`/management/dishes/${dishId}/toggle`, {
      is_available: isActive
    });
    return response.data;
    } catch (error) {
    console.error('Erreur lors de la mise à jour de la disponibilité:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour de la disponibilité');
  }
};

// Upload d'image pour un plat (CDN via /management/upload/image puis mise à jour du plat)
export const uploadDishImage = async (dishId, imageFile) => {
  try {
    const [dishesRes, formData] = await Promise.all([
      apiClient.get('/management/dishes'),
      (() => {
        const fd = new FormData();
        fd.append('file', imageFile);
        return fd;
      })()
    ]);
    const dish = (dishesRes.data || []).find((d) => d.id === dishId);
    if (!dish) throw new Error('Plat non trouvé');
    const uploadRes = await apiClient.post('/management/upload/image?folder=dishes', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const imageUrl = uploadRes.data?.image_url;
    if (!imageUrl) throw new Error('Aucune URL retournée après l\'upload');
    const response = await apiClient.put(`/management/dishes/${dishId}`, {
      name: dish.name,
      description: dish.description ?? '',
      price: dish.price,
      dish_image_url: imageUrl,
      category_id: dish.category_id ?? null,
      diet_type: dish.diet_type ?? 'none',
      is_available: dish.is_available !== false,
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de l\'upload de l\'image:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de l\'upload de l\'image');
  }
};

// === GESTION DES MENUS ===
// Utilise /management/menus pour cohérence avec l’API management (contexte restaurant via token)

// Récupérer tous les menus du restaurant
export const getRestaurantMenus = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/menus', { params });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des menus:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des menus');
  }
};

// Créer un nouveau menu
export const createMenu = async (menuData) => {
  try {
    const response = await apiClient.post('/management/menus', menuData);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la création du menu:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la création du menu');
  }
};

// Mettre à jour un menu
export const updateMenu = async (menuId, menuData) => {
  try {
    const response = await apiClient.put(`/management/menus/${menuId}`, menuData);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du menu:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour du menu');
  }
};

// Supprimer un menu
export const deleteMenu = async (menuId) => {
  try {
    const response = await apiClient.delete(`/management/menus/${menuId}`);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la suppression du menu:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la suppression du menu');
  }
};

// Basculer la disponibilité d'un menu (si l’API le supporte)
export const toggleMenuAvailability = async (menuId, isActive) => {
  try {
    const menus = await apiClient.get('/management/menus').then((r) => r.data || []);
    const menu = menus.find((m) => m.id === menuId);
    if (!menu) throw new Error('Menu non trouvé');
    const response = await apiClient.put(`/management/menus/${menuId}`, {
      name: menu.name,
      description: menu.description ?? null
    });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour de la disponibilité du menu:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour de la disponibilité du menu');
  }
};

// Récupérer les menus avec leurs plats
export const getRestaurantMenusWithDishes = async () => {
  try {
    const response = await apiClient.get('/management/menus-with-dishes');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des menus avec plats:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des menus avec plats');
  }
};

// Ajouter des plats à un menu
export const addDishesToMenu = async (menuId, dishIds) => {
  try {
    const response = await apiClient.post(`/management/menus/${menuId}/dishes/add`, { dish_ids: dishIds });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de l\'ajout de plats au menu:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de l\'ajout de plats au menu');
  }
};

// Retirer un plat d'un menu
export const removeDishFromMenu = async (menuId, dishId) => {
  try {
    const response = await apiClient.delete(`/management/menus/${menuId}/dishes/${dishId}`);
    return response.data;
  } catch (error) {
    console.error('Erreur lors du retrait du plat du menu:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors du retrait du plat du menu');
  }
};

// === GESTION DES CATÉGORIES ===

// Récupérer toutes les catégories
export const getCategories = async () => {
  try {
    const response = await apiClient.get('/categories/');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des catégories:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des catégories');
  }
};

// Créer une nouvelle catégorie
export const createCategory = async (categoryData) => {
  try {
    const response = await apiClient.post('/categories/', categoryData);
    return response.data;
    } catch (error) {
    console.error('Erreur lors de la création de la catégorie:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la création de la catégorie');
  }
};

// === GESTION DES SUPPLÉMENTS ===

// Récupérer les suppléments d'un plat
// Récupérer tous les suppléments du restaurant connecté
export const getAllSupplements = async () => {
  try {
    const response = await apiClient.get('/management/supplements');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des suppléments:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des suppléments');
  }
};

export const getDishSupplements = async (dishId) => {
  try {
    const response = await apiClient.get(`/supplements/dish/${dishId}`);
    return response.data;
    } catch (error) {
    console.error('Erreur lors de la récupération des suppléments:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des suppléments');
  }
};

// Créer un supplément
export const createSupplement = async (supplementData) => {
  try {
    const response = await apiClient.post('/supplements/', supplementData);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la création du supplément:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la création du supplément');
  }
};

// Mettre à jour un supplément
export const updateSupplement = async (supplementId, supplementData) => {
  try {
    const response = await apiClient.put(`/supplements/${supplementId}`, supplementData);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du supplément:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la mise à jour du supplément');
  }
};

// Ajouter un supplément à un plat (alias pour compatibilité)
export const addDishSupplement = async (dishId, supplementData) => {
  return createSupplement({ ...supplementData, dish_id: dishId });
};

// Supprimer un supplément d'un plat
export const removeDishSupplement = async (dishId, supplementId) => {
  try {
    const response = await apiClient.delete(`/supplements/${supplementId}`);
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la suppression du supplément:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la suppression du supplément');
  }
};

// === STATISTIQUES DU MENU ===

// Récupérer les statistiques du menu
export const getMenuStats = async () => {
  try {
    const response = await apiClient.get('/restaurant/menu/stats/');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des statistiques');
  }
};

// === FONCTIONS UTILITAIRES ===

export const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
};

export const getStatusColor = (status) => {
  switch ((status || '').toLowerCase()) {
    case 'pending': return 'text-yellow-700 bg-yellow-100';
    case 'preparing': return 'text-blue-700 bg-blue-100';
    case 'ready': return 'text-green-700 bg-green-100';
    case 'delivered': return 'text-gray-700 bg-gray-100';
    case 'cancelled': return 'text-red-700 bg-red-100';
    case 'pickup': return 'text-purple-700 bg-purple-100';
    default: return 'text-gray-700 bg-gray-100';
  }
};

// Formater le prix (arrondi à 2 décimales)
export const formatCurrency = (price) => {
  const roundedPrice = Math.round((price || 0) * 100) / 100; // Arrondir à 2 décimales
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(roundedPrice);
};

// Extraire un message lisible depuis detail (string ou tableau de validation FastAPI)
const getDetailMessage = (detail) => {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) return detail[0].msg || detail[0].message || JSON.stringify(detail[0]);
  return null;
};

// Gérer les erreurs de l'API
export const handleRestaurantApiError = (error) => {
  if (error.response) {
    // Erreur de réponse du serveur
    const status = error.response.status;
    const rawDetail = error.response.data?.detail ?? error.response.data?.message;
    const message = getDetailMessage(rawDetail) ?? (typeof rawDetail === 'string' ? rawDetail : null);
    
    switch (status) {
      case 400:
        return message || 'Données invalides';
      case 401:
        return 'Non autorisé. Veuillez vous reconnecter.';
      case 403:
        return 'Accès refusé';
      case 404:
        return 'Ressource non trouvée';
      case 422:
        return message || 'Erreur de validation des données';
      case 500:
        return 'Erreur interne du serveur';
      default:
        return message || 'Une erreur est survenue';
    }
  } else if (error.request) {
    // Erreur de réseau
    return 'Erreur de connexion. Vérifiez votre connexion internet.';
  } else {
    // Autre erreur
    return error.message || 'Une erreur inattendue est survenue';
  }
};

// Valider les données d'un plat
export const validateDishData = (dishData) => {
  const errors = [];
  
  if (!dishData.name || dishData.name.trim().length < 2) {
    errors.push('Le nom du plat doit contenir au moins 2 caractères');
  }
  
  if (!dishData.description || dishData.description.trim().length < 10) {
    errors.push('La description doit contenir au moins 10 caractères');
  }
  
  if (!dishData.price || dishData.price <= 0) {
    errors.push('Le prix doit être supérieur à 0');
  }
  
  if (!dishData.category) {
    errors.push('La catégorie est obligatoire');
  }
  
  if (!dishData.preparation_time || dishData.preparation_time <= 0) {
    errors.push('Le temps de préparation doit être supérieur à 0');
  }
  
  return errors;
};

// Valider les données d'un menu
export const validateMenuData = (menuData) => {
  const errors = [];
  
  if (!menuData.name || menuData.name.trim().length < 2) {
    errors.push('Le nom du menu doit contenir au moins 2 caractères');
  }
  
  if (!menuData.description || menuData.description.trim().length < 10) {
    errors.push('La description doit contenir au moins 10 caractères');
  }
  
  if (!menuData.price || menuData.price <= 0) {
    errors.push('Le prix doit être supérieur à 0');
  }
  
  if (!menuData.dishes || menuData.dishes.length === 0) {
    errors.push('Le menu doit contenir au moins un plat');
  }
  
  return errors;
};

// ===== DASHBOARD & STATISTIQUES =====

// Récupérer les statistiques du dashboard
export const getRestaurantDashboardStats = async () => {
  try {
    const response = await apiClient.get('/management/stats/dashboard');
    return response.data;
    } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// === ANALYTICS ===

// Récupérer les analytics générales
export const getAnalytics = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/analytics', { params });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des analytics:', error);
    throw handleRestaurantApiError(error);
  }
};

// Récupérer les analytics des revenus
export const getRevenueAnalytics = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/analytics/revenue', { params });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des analytics de revenus:', error);
    throw handleRestaurantApiError(error);
  }
};

// Récupérer les analytics des commandes
export const getOrdersAnalytics = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/analytics/orders', { params });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des analytics de commandes:', error);
    throw handleRestaurantApiError(error);
  }
};

// Récupérer les analytics des clients
export const getCustomersAnalytics = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/analytics/customers', { params });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des analytics de clients:', error);
    throw handleRestaurantApiError(error);
  }
};

// Récupérer les analytics des plats
export const getDishesAnalytics = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/analytics/dishes', { params });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des analytics de plats:', error);
    throw handleRestaurantApiError(error);
  }
};

// Récupérer les analytics des suppléments
export const getSupplementsAnalytics = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/analytics/supplements', { params });
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des analytics de suppléments:', error);
    throw handleRestaurantApiError(error);
  }
};

// ===== COMMANDES =====

// Récupérer les commandes du restaurant
export const getRestaurantOrders = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/orders', { params });
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Mettre à jour le statut d'une commande
export const updateOrderStatus = async (orderId, status) => {
  try {
    const response = await apiClient.put(`/management/orders/${orderId}/status`, { status });
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// ===== SERVICES DE TRAITEUR =====

// Récupérer les services de traiteur
export const getCateringServices = async () => {
  try {
    const response = await apiClient.get('/management/catering-services');
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Récupérer un service traiteur par son ID (endpoint public, ne filtre pas par is_active)
export const getCateringServiceById = async (serviceId) => {
  try {
    const response = await apiClient.get(`/catering/services/${serviceId}`);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

/** Upload image CDN (DigitalOcean Spaces) pour un service traiteur — renvoie l'URL publique */
export const uploadCateringServiceImage = async (imageFile) => {
  const formData = new FormData();
  formData.append('file', imageFile);
  const uploadRes = await apiClient.post('/management/upload/image?folder=catering', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const imageUrl = uploadRes.data?.image_url;
  if (!imageUrl) throw new Error('Aucune URL retournée après l\'upload');
  return imageUrl;
};

// Créer un service de traiteur
export const createCateringService = async (serviceData) => {
  try {
    const response = await apiClient.post('/management/catering-services', serviceData);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Mettre à jour un service de traiteur
export const updateCateringService = async (serviceId, serviceData) => {
  try {
    const response = await apiClient.put(`/management/catering-services/${serviceId}`, serviceData);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Supprimer un service de traiteur
export const deleteCateringService = async (serviceId) => {
  try {
    const response = await apiClient.delete(`/management/catering-services/${serviceId}`);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// ===== RÉSERVATIONS TRAITEUR =====

// Récupérer les réservations de traiteur
export const getCateringBookings = async (statusFilter = 'all') => {
  try {
    const params = statusFilter !== 'all' ? { status_filter: statusFilter } : {};
    const response = await apiClient.get('/catering/bookings', { params });
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Mettre à jour le statut d'une réservation
export const updateCateringBookingStatus = async (bookingId, status) => {
  try {
    const formData = new FormData();
    formData.append('status', status);
    const response = await apiClient.put(`/catering/bookings/${bookingId}/status`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Récupérer les statistiques de traiteur
export const getCateringStats = async () => {
  try {
    const response = await apiClient.get('/management/catering-services/stats');
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// ===== GESTION DES EMPLOYÉS =====

// Récupérer les employés
export const getEmployees = async () => {
  try {
    const response = await apiClient.get('/management/employees');
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Créer un employé
export const createEmployee = async (employeeData) => {
  try {
    const response = await apiClient.post('/management/employees', employeeData);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Mettre à jour un employé
export const updateEmployee = async (employeeId, employeeData) => {
  try {
    const response = await apiClient.put(`/management/employees/${employeeId}`, employeeData);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Supprimer un employé
export const deleteEmployee = async (employeeId) => {
  try {
    const response = await apiClient.delete(`/management/employees/${employeeId}`);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Basculer le statut d'un employé
export const toggleEmployeeStatus = async (employeeId) => {
  try {
    const response = await apiClient.patch(`/management/employees/${employeeId}/toggle-status`);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// ===== PROMOTIONS =====

// Récupérer les promotions
export const getPromotions = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/promotions', { params });
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Créer une promotion
export const createPromotion = async (promotionData) => {
  try {
    const response = await apiClient.post('/management/promotions', promotionData);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Mettre à jour une promotion
export const updatePromotion = async (promotionId, promotionData) => {
  try {
    const response = await apiClient.put(`/management/promotions/${promotionId}`, promotionData);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Supprimer une promotion
export const deletePromotion = async (promotionId) => {
  try {
    const response = await apiClient.delete(`/management/promotions/${promotionId}`);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// ===== AVIS & COMMENTAIRES =====

// Récupérer les avis
export const getReviews = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/reviews', { params });
    return response.data;
    } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Répondre à un avis
export const respondToReview = async (reviewId, responseData) => {
  try {
    const response = await apiClient.post(`/management/reviews/${reviewId}/reply`, responseData);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// === ANNONCES ===
export const getAnnouncements = async () => {
  try {
    const response = await apiClient.get('/management/announcements');
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des annonces:', error);
    throw new Error(error.response?.data?.detail || 'Erreur lors de la récupération des annonces');
  }
};

// ===== SÉCURITÉ COMPTE =====

export const get2FAStatus = async () => {
  try {
    const response = await apiClient.get('/auth/2fa/status');
    return response.data;
  } catch {
    return { enabled: false };
  }
};

export const enable2FA = async (currentPassword) => {
  try {
    const response = await apiClient.post('/auth/2fa/enable', { current_password: currentPassword });
    return response.data;
  } catch (error) {
    const detail = error?.response?.data?.detail;
    throw new Error(typeof detail === 'string' ? detail : 'Erreur activation 2FA');
  }
};

export const disable2FA = async (currentPassword) => {
  try {
    const response = await apiClient.post('/auth/2fa/disable', { current_password: currentPassword });
    return response.data;
  } catch (error) {
    const detail = error?.response?.data?.detail;
    throw new Error(typeof detail === 'string' ? detail : 'Erreur désactivation 2FA');
  }
};

export const send2FACode = async () => {
  try {
    const response = await apiClient.post('/auth/2fa/send-code', {});
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

export const verify2FALogin = async (tempToken, code) => {
  const response = await fetch(`${apiClient.defaults.baseURL}/auth/2fa/verify-login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ temp_token: tempToken, code }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || 'Code invalide');
  return data;
};

export const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await apiClient.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  } catch (error) {
    const detail = error?.response?.data?.detail;
    throw new Error(typeof detail === 'string' ? detail : 'Erreur lors du changement de mot de passe');
  }
};

export const getActiveSessions = async () => {
  try {
    const response = await apiClient.get('/auth/sessions');
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

export const terminateSession = async (sessionId) => {
  try {
    const response = await apiClient.delete(`/auth/sessions/${sessionId}`);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

export const terminateAllSessions = async (currentPassword) => {
  try {
    const response = await apiClient.post('/auth/logout-all', { current_password: currentPassword });
    return response.data;
  } catch (error) {
    const detail = error?.response?.data?.detail;
    throw new Error(typeof detail === 'string' ? detail : 'Erreur lors de la déconnexion');
  }
};

// ===== PORTEFEUILLE =====

// Récupérer les informations du portefeuille
export const getWalletInfo = async () => {
  try {
    const response = await apiClient.get('/management/wallet');
    return response.data;
    } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Récupérer les transactions
export const getTransactions = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/wallet/transactions', { params });
    return response.data;
    } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Demander un paiement
export const requestPayout = async (payoutData) => {
  try {
    const response = await apiClient.post('/restaurant/payout-request/', payoutData);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Alias pour compatibilité
export const getRestaurantWallet = getWalletInfo;
export const getWalletTransactions = getTransactions;

// Retirer de l'argent du portefeuille
export const withdrawFromWallet = async (withdrawalData) => {
  try {
    const response = await apiClient.post('/management/wallet/withdraw', withdrawalData);
    return response.data;
  } catch (error) {
    console.error('Erreur lors du retrait:', error);
    throw handleRestaurantApiError(error);
  }
};

// Coordonnées bancaires (IBAN) pour les retraits
export const getWalletBankDetails = async () => {
  try {
    const response = await apiClient.get('/management/wallet/bank-details');
    return response.data;
  } catch (error) {
    console.error('Erreur chargement coordonnées bancaires:', error);
    throw handleRestaurantApiError(error);
  }
};

export const updateWalletBankDetails = async (data) => {
  try {
    const response = await apiClient.put('/management/wallet/bank-details', data);
    return response.data;
  } catch (error) {
    console.error('Erreur sauvegarde coordonnées bancaires:', error);
    throw handleRestaurantApiError(error);
  }
};

// ===== LEDGER (nouvelle architecture paiement) =====

export const getLedgerBalance = async () => {
  try {
    const response = await apiClient.get('/management/ledger/balance');
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

export const getLedgerEntries = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/ledger/entries', { params });
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

export const getLedgerPayouts = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/ledger/payouts', { params });
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

export const requestLedgerWithdrawal = async () => {
  try {
    const response = await apiClient.post('/management/ledger/withdraw');
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// ===== PAIEMENTS =====

// Traiter un paiement complet
export const processCompletePayment = async (paymentData) => {
  try {
    const response = await apiClient.post('/complete-payment/process', paymentData);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// ===== VALIDATION DES DONNÉES =====

// Valider les données d'un service de traiteur
// === GESTION DU PROFIL ET PARAMÈTRES ===

// Récupérer le profil du restaurant
export const getRestaurantProfile = async () => {
  try {
    const response = await apiClient.get('/management/profile');
    return response.data;
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

// Mettre à jour le profil du restaurant
export const updateRestaurantProfile = async (profileData) => {
  try {
    const response = await apiClient.put('/management/profile', profileData);
    return response.data;
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

// === DOCUMENTS DE CERTIFICATION ===

/** Envoie un document justificatif (KBIS, certificat sanitaire, pièce d'identité, assurance...) */
export const uploadRestaurantDocument = async (documentType, file) => {
  try {
    const fd = new FormData();
    fd.append('document_type', documentType);
    fd.append('file', file);
    const response = await apiClient.post('/management/documents', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

/** Liste les documents de certification déjà envoyés par le restaurant connecté */
export const getMyRestaurantDocuments = async () => {
  try {
    const response = await apiClient.get('/management/documents');
    return response.data;
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

/** Magasin grocery lié au restaurant (GET /management/grocery-shop), ou null */
export const getManagementGroceryShop = async () => {
  try {
    const response = await apiClient.get('/management/grocery-shop');
    return response.data;
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

/** Produits du magasin lié (GET /management/grocery-shop/products) */
export const getManagementGroceryShopProducts = async (params = {}) => {
  try {
    const response = await apiClient.get('/management/grocery-shop/products', { params });
    return response.data;
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

/** Crée le magasin grocery du restaurant connecté (POST /management/grocery-shop) */
export const createManagementGroceryShop = async (shopData) => {
  try {
    const response = await apiClient.post('/management/grocery-shop', shopData);
    return response.data;
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

/** Met à jour le magasin grocery du restaurant connecté (PUT /management/grocery-shop) */
export const updateManagementGroceryShop = async (shopData) => {
  try {
    const response = await apiClient.put('/management/grocery-shop', shopData);
    return response.data;
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

/** Crée un produit dans le magasin grocery du restaurant connecté */
export const createManagementGroceryShopProduct = async (productData) => {
  try {
    const response = await apiClient.post('/management/grocery-shop/products', productData);
    return response.data;
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

/** Met à jour un produit du magasin grocery du restaurant connecté */
export const updateManagementGroceryShopProduct = async (productId, productData) => {
  try {
    const response = await apiClient.put(`/management/grocery-shop/products/${productId}`, productData);
    return response.data;
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

/** Supprime un produit du magasin grocery du restaurant connecté */
export const deleteManagementGroceryShopProduct = async (productId) => {
  try {
    await apiClient.delete(`/management/grocery-shop/products/${productId}`);
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

// ===== STRIPE CONNECT (RESTAURANT) =====
const getCurrentRestaurantId = async () => {
  const profile = await getRestaurantProfile();
  const restaurantId = profile?.id ?? profile?.restaurant_id;
  if (!restaurantId) {
    throw new Error('Restaurant introuvable pour Stripe Connect');
  }
  return restaurantId;
};

export const getStripeConnectStatus = async () => {
  try {
    const response = await apiClient.get('/management/stripe-connect/status');
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

export const getStripeConnectBalance = async () => {
  try {
    const restaurantId = await getCurrentRestaurantId();
    const response = await apiClient.get(`/stripe-connect/balance/${restaurantId}`);
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

export const getStripeConnectPayouts = async (limit = 20) => {
  try {
    const restaurantId = await getCurrentRestaurantId();
    const response = await apiClient.get(`/stripe-connect/payouts/${restaurantId}`, {
      params: { limit },
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

export const getStripeConnectPayments = async (limit = 20) => {
  try {
    const restaurantId = await getCurrentRestaurantId();
    const response = await apiClient.get(`/stripe-connect/payments/${restaurantId}`, {
      params: { limit },
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

export const requestStripeConnectWithdrawal = async (amount, reason = 'Retrait de fonds') => {
  try {
    const restaurantId = await getCurrentRestaurantId();
    const response = await apiClient.post(`/stripe-connect/withdraw/${restaurantId}`, {
      amount,
      reason,
    });
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

export const createStripeConnectOnboardingLink = async () => {
  try {
    const response = await apiClient.post('/management/stripe-connect/onboarding-link');
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

export const createStripeConnectDashboardLink = async () => {
  try {
    const response = await apiClient.post('/management/stripe-connect/dashboard-link');
    return response.data;
  } catch (error) {
    throw handleRestaurantApiError(error);
  }
};

// Statut Stripe Connect via endpoint management (ne nécessite pas l'ID restaurant)
export const getManagementConnectStatus = async () => {
  try {
    const response = await apiClient.get('/management/wallet/connect-status');
    return response.data;
  } catch {
    return { has_connect_account: false, payouts_enabled: false, charges_enabled: false, details_submitted: false };
  }
};

// Démarrer l'onboarding Stripe Connect depuis les paramètres
export const startConnectOnboarding = async (opts = {}) => {
  const refreshUrl = opts.refreshUrl || `${window.location.origin}/management/settings?connect=refresh`;
  const returnUrl = opts.returnUrl || `${window.location.origin}/management/settings?connect=done`;
  try {
    const response = await apiClient.post('/management/wallet/connect-onboarding', {
      refresh_url: refreshUrl,
      return_url: returnUrl,
    });
    return response.data;
  } catch (error) {
    const detail = error?.response?.data?.detail;
    throw new Error(typeof detail === 'string' ? detail : 'Impossible de créer le lien Stripe');
  }
};

// Sauvegarder les paramètres de retrait automatique
export const saveAutoWithdraw = async (enabled, threshold) => {
  const response = await apiClient.put('/management/wallet/auto-withdraw', {
    auto_withdraw_enabled: enabled,
    auto_withdraw_threshold: threshold,
  });
  return response.data;
};

// Récupérer les horaires du restaurant
export const getRestaurantHours = async () => {
  try {
    const response = await apiClient.get('/management/hours');
    return response.data;
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

// Mettre à jour les horaires du restaurant
export const updateRestaurantHours = async (hoursData) => {
  try {
    const response = await apiClient.put('/management/hours', hoursData);
    return response.data;
  } catch (error) {
    handleRestaurantApiError(error);
    throw error;
  }
};

export const validateCateringServiceData = (data) => {
  const errors = [];
  
  if (!data.name || data.name.trim().length < 2) {
    errors.push('Le nom du service doit contenir au moins 2 caractères');
  }
  
  if (!data.description || data.description.trim().length < 10) {
    errors.push('La description doit contenir au moins 10 caractères');
  }
  
  if (!data.price || data.price <= 0) {
    errors.push('Le prix doit être supérieur à 0');
  }
  
  if (!data.min_guests || data.min_guests < 1) {
    errors.push('Le nombre minimum d\'invités doit être au moins 1');
  }
  
  return errors;
};

// Valider les données d'un employé
export const validateEmployeeData = (data) => {
  const errors = [];
  
  if (!data.first_name || data.first_name.trim().length < 2) {
    errors.push('Le prénom doit contenir au moins 2 caractères');
  }
  
  if (!data.last_name || data.last_name.trim().length < 2) {
    errors.push('Le nom doit contenir au moins 2 caractères');
  }
  
  if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.push('L\'email doit être valide');
  }
  
  if (!data.phone || data.phone.trim().length < 10) {
    errors.push('Le numéro de téléphone doit contenir au moins 10 caractères');
  }
  
  if (!data.position || data.position.trim().length < 2) {
    errors.push('Le poste doit contenir au moins 2 caractères');
  }
  
  return errors;
};

// Valider les données d'une promotion
export const validatePromotionData = (data) => {
  const errors = [];
  
  if (!data.name || data.name.trim().length < 2) {
    errors.push('Le nom de la promotion doit contenir au moins 2 caractères');
  }
  
  if (!data.description || data.description.trim().length < 10) {
    errors.push('La description doit contenir au moins 10 caractères');
  }
  
  if (!data.discount_percentage || data.discount_percentage <= 0 || data.discount_percentage > 100) {
    errors.push('Le pourcentage de réduction doit être entre 1 et 100');
  }
  
  if (!data.start_date) {
    errors.push('La date de début est requise');
  }
  
  if (!data.end_date) {
    errors.push('La date de fin est requise');
  }
  
  if (data.start_date && data.end_date && new Date(data.start_date) >= new Date(data.end_date)) {
    errors.push('La date de fin doit être après la date de début');
  }
  
  return errors;
};

export default {
  // Plats
  getRestaurantDishes,
  createDish,
  updateDish,
  deleteDish,
  toggleDishAvailability,
  uploadDishImage,
  
  // Menus
  getRestaurantMenus,
  createMenu,
  updateMenu,
  deleteMenu,
  toggleMenuAvailability,
  
  // Catégories
  getCategories,
  createCategory,
  
  // Suppléments
  getDishSupplements,
  addDishSupplement,
  removeDishSupplement,
  
  // Statistiques
  getMenuStats,
  getRestaurantDashboardStats,
  
  // Commandes
  getRestaurantOrders,
  updateOrderStatus,
  
  // Services de traiteur
  getCateringServices,
  createCateringService,
  updateCateringService,
  deleteCateringService,
  uploadCateringServiceImage,
  
  // Employés
  getEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  toggleEmployeeStatus,
  
  // Promotions
  getPromotions,
  createPromotion,
  updatePromotion,
  deletePromotion,
  
  // Avis
  getReviews,
  respondToReview,
  
  // Annonces
  getAnnouncements,
  
  // Portefeuille
  getWalletInfo,
  getTransactions,
  requestPayout,
  getRestaurantWallet,
  getWalletTransactions,
  withdrawFromWallet,

  // Ledger (nouvelle architecture paiement)
  getLedgerBalance,
  getLedgerEntries,
  getLedgerPayouts,
  requestLedgerWithdrawal,

  // Analytics
  getAnalytics,
  getRevenueAnalytics,
  getOrdersAnalytics,
  getCustomersAnalytics,
  getDishesAnalytics,
  getSupplementsAnalytics,
  
  // Menu avec plats
  getRestaurantMenusWithDishes,
  addDishesToMenu,
  removeDishFromMenu,
  
  // Paiements
  processCompletePayment,
  
  // Paramètres et profil
  getRestaurantProfile,
  updateRestaurantProfile,
  getManagementGroceryShop,
  getManagementGroceryShopProducts,
  createManagementGroceryShop,
  updateManagementGroceryShop,
  createManagementGroceryShopProduct,
  updateManagementGroceryShopProduct,
  deleteManagementGroceryShopProduct,
  getRestaurantHours,
  updateRestaurantHours,
  getStripeConnectStatus,
  getStripeConnectBalance,
  getStripeConnectPayouts,
  getStripeConnectPayments,
  requestStripeConnectWithdrawal,
  createStripeConnectOnboardingLink,
  
  // Utilitaires
  formatCurrency,
  formatDateTime,
  getStatusColor,
  handleRestaurantApiError,
  validateDishData,
  validateMenuData,
  validateCateringServiceData,
  validateEmployeeData,
  validatePromotionData
};